/**
 * "Work in flight" gate for anything a page reload would destroy, such as a video upload. While at least one
 * hold is open the browser's leave-page prompt is armed (so a reload, including lazyRetry's recovery reload,
 * asks first), and the in-app update notice disables its Refresh button. No browser global is touched at module
 * scope, so the gate is unit-tested in Node.
 */

let holds = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function onBeforeUnload(e: BeforeUnloadEvent): void {
  e.preventDefault()
  e.returnValue = '' // some browsers only show the prompt when a value is set
}

/** Open a hold. Call the returned release (safe to call more than once) in a finally when the work ends. */
export function holdBusy(): () => void {
  holds += 1
  if (holds === 1 && typeof window !== 'undefined') window.addEventListener('beforeunload', onBeforeUnload)
  emit()
  let released = false
  return () => {
    if (released) return
    released = true
    holds -= 1
    if (holds === 0 && typeof window !== 'undefined') window.removeEventListener('beforeunload', onBeforeUnload)
    emit()
  }
}

export const isBusy = (): boolean => holds > 0

/** Hold the gate for the length of an async job a reload would ruin (an upload). The single place callers wrap. */
export async function whileBusy<T>(work: () => Promise<T>): Promise<T> {
  const release = holdBusy()
  try {
    return await work()
  } finally {
    release()
  }
}

export function subscribeBusy(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
